import { useQuery } from '@tanstack/react-query';
import { apiFetch, StatCard, Skeleton, EmptyState } from './shared';

interface OrganicData {
  stats: {
    organic_clicks: number;
    organic_signups: number;
    signup_rate: number;
    organic_members: number;
    direct_clicks: number;
    search_clicks: number;
  };
  top_landing_pages: Array<{
    landing_page: string | null;
    clicks: number;
    signups: number;
    signup_rate: number;
  }>;
  source_breakdown: Array<{ utm_source: string | null; clicks: number }>;
}

export default function OrganicSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ua', 'organic'],
    queryFn: () => apiFetch('/api/ua/admin/organic') as Promise<OrganicData>,
  });

  if (isLoading || !data) return <Skeleton className="h-96" />;

  const { stats, top_landing_pages, source_breakdown } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Organic Clicks" value={stats.organic_clicks} />
        <StatCard label="Organic Signups" value={stats.organic_signups} tone="info" />
        <StatCard label="Signup Rate" value={`${stats.signup_rate}%`} />
        <StatCard label="Organic Members" value={stats.organic_members} sub="No attributed campaign" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard label="Direct (no UTM)" value={stats.direct_clicks} sub="Clicks with no utm_source" />
        <StatCard label="Search engine" value={stats.search_clicks} sub="google/bing/duckduckgo/yahoo/baidu" />
      </div>

      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        <div className="px-5 py-3 border-b border-slate-700">
          <h3 className="text-sm font-bold text-white">Top Landing Pages</h3>
          <p className="text-xs text-slate-500 mt-0.5">Clicks from organic traffic (no campaign, no ref code).</p>
        </div>
        {top_landing_pages.length === 0 ? (
          <div className="text-center text-slate-500 py-10 text-sm">No organic landing data yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                <th className="px-3 py-2">Landing Page</th>
                <th className="px-3 py-2 text-right">Clicks</th>
                <th className="px-3 py-2 text-right">Signups</th>
                <th className="px-3 py-2 text-right">Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {top_landing_pages.map((row, i) => (
                <tr key={`${row.landing_page}-${i}`} className="hover:bg-slate-700/50">
                  <td className="px-3 py-2 text-slate-300 text-xs font-mono truncate max-w-[400px]">{row.landing_page ?? '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{row.clicks}</td>
                  <td className="px-3 py-2 text-right text-green-400 tabular-nums">{row.signups}</td>
                  <td className="px-3 py-2 text-right text-white tabular-nums">{row.signup_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        <div className="px-5 py-3 border-b border-slate-700">
          <h3 className="text-sm font-bold text-white">Traffic Source Breakdown</h3>
          <p className="text-xs text-slate-500 mt-0.5">Untagged utm_source values — implicit organic (no campaign match).</p>
        </div>
        {source_breakdown.length === 0 ? (
          <EmptyState title="No untagged UTM traffic" body="All clicks with utm_source match an active campaign." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                <th className="px-3 py-2">utm_source</th>
                <th className="px-3 py-2 text-right">Clicks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {source_breakdown.map((row, i) => (
                <tr key={`${row.utm_source}-${i}`} className="hover:bg-slate-700/50">
                  <td className="px-3 py-2 text-slate-300 font-mono text-xs">{row.utm_source ?? '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{row.clicks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
